import {
	Table,
	TableContainer,
	TableRow,
	TableHead,
	TableBody,
	TableCell,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { CerebroHeading, CerebroIcon, CerebroIconButton, CerebroText } from '.';
import { CreditOutputLineItem, OutputAnalysisGroup, OutputAnalysisItem } from '../models';
import { EnumUtils } from '../utils';
import { ReactComponent as GreenCheck } from '../icons/GreenCheck.svg';
import { ReactComponent as YellowCheck } from '../icons/YellowCheck.svg';
import { ReactComponent as RedExclamation } from '../icons/RedExclamation.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faBan, faEyeSlash, faEdit } from '@fortawesome/pro-duotone-svg-icons';

interface OutputAnalysisTableProps {
	group: OutputAnalysisGroup;
	outputItems: OutputAnalysisItem[];
	items: CreditOutputLineItem[];
	isEditMode: boolean;
	isVisibleToBorrower: boolean;
	onViewOutputAnalysis: (output: OutputAnalysisItem, item: CreditOutputLineItem | null) => void;
	onEditAnalysisItem: (original: OutputAnalysisItem, item: CreditOutputLineItem | null) => void;
}

export const OutputAnalysisTable: React.FC<OutputAnalysisTableProps> = ({
	group,
	outputItems,
	items,
	isEditMode,
	isVisibleToBorrower,
	onViewOutputAnalysis,
	onEditAnalysisItem,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	if (!isEditMode && !isVisibleToBorrower) {
		return <></>;
	}

	return outputItems != null && outputItems.length > 0 ? (
		<>
			<CerebroHeading component="h1" size="h1" sx={{ marginBottom: 3 }}>
				{EnumUtils.GetOutputAnalysisGroupDescription(group)}

				{isEditMode && !isVisibleToBorrower && (
					<FontAwesomeIcon
						icon={faEyeSlash}
						size="xs"
						style={{ marginLeft: 5 }}
						title="Hidden from borrower"
					/>
				)}
			</CerebroHeading>

			<TableContainer sx={{ marginBottom: 3 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell width={200}>
								<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
									Borrower Profile
								</CerebroText>
							</TableCell>
							{isEditMode && <TableCell></TableCell>}
							{isMobile && (
								<TableCell>
									<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
										Rating
									</CerebroText>
								</TableCell>
							)}
							{!isMobile && (
								<>
									<TableCell width={120}>
										<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
											Very Strong
											<br />
											Low Risk
										</CerebroText>
									</TableCell>
									<TableCell width={100}>
										<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
											Strong
										</CerebroText>
									</TableCell>
									<TableCell width={100}>
										<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
											Stable
											<br />
											Neutral
										</CerebroText>
									</TableCell>
									<TableCell width={100}>
										<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
											Weak
											<br />
											High Risk
										</CerebroText>
									</TableCell>
								</>
							)}
							<TableCell width={100}>
								<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
									Value
								</CerebroText>
							</TableCell>
							{!isMobile && (
								<TableCell>
									<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
										Comments
									</CerebroText>
								</TableCell>
							)}
							{isEditMode && <TableCell></TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{outputItems.map(output => {
							const overrideOutput = items.filter(w => w.outputType == output.outputType);
							const item = overrideOutput && overrideOutput.length > 0 ? overrideOutput[0] : null;
							const isVisible = item ? item.isVisibleToBorrower : true;
							const outputStatus = item ? item.outputStatus : output.outputStatus;
							const comments = item ? item.comments : output.comments;

							if (!isEditMode && !isVisible) {
								return <></>;
							}

							return (
								<TableRow key={output.outputType}>
									<TableCell>
										<CerebroText variant="hint">
											{EnumUtils.GetOutputAnalysisTypeDescription(output.outputType)}
											{!isVisible && (
												<FontAwesomeIcon
													icon={faEyeSlash}
													size="xs"
													style={{ marginLeft: 5 }}
													title="Hidden from borrower"
												/>
											)}
										</CerebroText>
									</TableCell>
									{isEditMode && (
										<TableCell>
											<CerebroIcon
												sx={{ cursor: 'pointer' }}
												title="Click to view analysis details"
												onClick={() => onViewOutputAnalysis(output, item)}
											>
												<FontAwesomeIcon icon={faInfoCircle} size="lg" />
											</CerebroIcon>
										</TableCell>
									)}
									{isMobile && (
										<TableCell>
											{EnumUtils.GetOutputAnalysisStatusDescription(outputStatus)}
										</TableCell>
									)}
									{!isMobile && (
										<>
											<TableCell>{outputStatus == 'VeryStrong' && <GreenCheck />}</TableCell>
											<TableCell>{outputStatus == 'Strong' && <GreenCheck />}</TableCell>
											<TableCell>{outputStatus == 'Neutral' && <YellowCheck />}</TableCell>
											<TableCell>{outputStatus == 'Weak' && <RedExclamation />}</TableCell>
										</>
									)}

									<TableCell>
										{outputStatus == 'MissingData' ? (
											<FontAwesomeIcon icon={faBan} size="sm" title="Missing Data" />
										) : (
											<CerebroText variant="hint">{output.value}</CerebroText>
										)}
									</TableCell>
									{!isMobile && (
										<TableCell>
											<CerebroText variant="hint">{comments}</CerebroText>
										</TableCell>
									)}
									{isEditMode && (
										<TableCell>
											<CerebroIconButton
												color="info"
												onClick={() => onEditAnalysisItem(output, item)}
												title="To edit the analysis row details"
											>
												<FontAwesomeIcon icon={faEdit} size="xs" />
											</CerebroIconButton>
										</TableCell>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	) : (
		<></>
	);
};
